<template>
  <div class="dashboard-view wh100">
    <header class="flex flex-between">
      <div v-if="data.name"
           class="flex">
        <h1>
          {{ data.name }}
        </h1>
      </div>

      <div class="flex m-left-auto">
        <BaseSelect name="entity"
                    label="entity"
                    cy="entity-selector"
                    @select="handleEntitySelect"
                    objectKey="name"
                    :options="entities" />

        <div class="date-picker flex">
          <Datepicker
              @update:model-value="handleStartDate($event)"
              placeholder="start date"
              :enable-time-picker="false"
              :model-value="startDate"
          />
          <i @click="setTodayDate"
             class="pointer"
             cy="set-today-date">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="4" fill="white"/>
              <path d="M13.2745 20.6941C12.5425 20.6941 11.9237 20.4325 11.4183 19.9094C10.9129 19.3863 10.6601 18.7459 10.6601 17.9882C10.6601 17.2306 10.9129 16.5902 11.4183 16.0671C11.9237 15.5439 12.5425 15.2824 13.2745 15.2824C14.0065 15.2824 14.6253 15.5439 15.1307 16.0671C15.6362 16.5902 15.8889 17.2306 15.8889 17.9882C15.8889 18.7459 15.6362 19.3863 15.1307 19.9094C14.6253 20.4325 14.0065 20.6941 13.2745 20.6941ZM9.0915 26.6471C8.51634 26.6471 8.02414 26.4353 7.6149 26.0117C7.20566 25.5882 7.0007 25.0784 7 24.4824V9.32941C7 8.73412 7.20497 8.22469 7.6149 7.80113C8.02484 7.37757 8.51704 7.16543 9.0915 7.16471H10.1373V5H12.2288V7.16471H20.5948V5H22.6863V7.16471H23.732C24.3072 7.16471 24.7997 7.37685 25.2097 7.80113C25.6196 8.22541 25.8242 8.73484 25.8235 9.32941V24.4824C25.8235 25.0776 25.6189 25.5874 25.2097 26.0117C24.8004 26.436 24.3079 26.6478 23.732 26.6471H9.0915ZM9.0915 24.4824H23.732V13.6588H9.0915V24.4824ZM9.0915 11.4941H23.732V9.32941H9.0915V11.4941Z" fill="#0F91D2"/>
            </svg>
          </i>
        </div>

        <BaseSelect name="cadence"
                    label="cadence"
                    cy="select-cadence"
                    @select="handleCadence"
                    :value="cadence"
                    :options="cadences" />
      </div>
    </header>

    <div class="entity-columns m-t-24 flex">
      <div v-if="Object.keys(data)?.length"
           class="flex"
           cy="entity-columns">

        <section class="entity-column relative"
             v-for="(result, index) in results"
             :key="index">

          <div v-if="filename === result.filename"
               class="img-preview absolute">
            <i @click="closePreview"
               class="absolute right0 top0 pointer">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 0.5C5.15 0.5 0.5 5.15 0.5 11C0.5 16.85 5.15 21.5 11 21.5C16.85 21.5 21.5 16.85 21.5 11C21.5 5.15 16.85 0.5 11 0.5ZM15.05 16.25L11 12.2L6.95 16.25L5.75 15.05L9.8 11L5.75 6.95L6.95 5.75L11 9.8L15.05 5.75L16.25 6.95L12.2 11L16.25 15.05L15.05 16.25Z" fill="black"/>
              </svg>
            </i>
            <figure class="h100">
              <img :src="`/img/${route.query._id}/${filename}`" alt="" />
            </figure>
          </div>

          <ColumnHeader :id="result.id"
                        :date="result.scrapedAt"
                        :image="result.filename"
                        @action="handleAction" />

          <div class="flex">
            <ColumnLeftSide :data="result.leftSide"
                            :column-id="result.id" />
            <ColumnRightSide v-if="result.rightSide?.length"
                             :column-id="result.id"
                             :data="result.rightSide" />
          </div>

        </section>
      </div>

      <div v-else
           class="select-entity-bg-text wh100 flex-center">
        Select an entity
      </div>
    </div>
  </div>
</template>

<script setup>
  import {computed, ref, onUnmounted} from "vue";
  import {useStore} from "vuex";
  import router from "@/router";
  import {useRoute} from "vue-router";
  import BaseSelect from "@/components/BaseSelect.vue";
  import Datepicker from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';
  import ColumnHeader from "@/components/ColumnHeader.vue";
  import ColumnLeftSide from "@/components/ColumnLeftSide.vue";
  import ColumnRightSide from "@/components/ColumnRightSide.vue";
  import {formatDate} from "@/store/utils";

  const { state, dispatch, commit, getters } = useStore()
  dispatch('get_entities')
  commit('RESET_ENTITY')
  const data = computed(() => state.dashboard.data)
  const entities = computed(() => state.settings.entities?.filter(obj => obj.name))
  const cadence = computed(() => state.dashboard.cadence)
  const results = computed(() => getters.results)
  const cadences = ['daily', 'weekly', 'monthly']
  const startDate = computed(() => state.dashboard.startDate)
  const filename = ref('')
  const route = useRoute()
  if (route.query._id) {
    dispatch('get_entity_data', route.query._id)
  }

  function setTodayDate () {
    commit('SET_START_DATE', formatDate())
  }

  function handleEntitySelect ({value}) {
    commit('SET_START_DATE', '01/01/2024')
    commit('SET_D_STATE', {key: 'cadence', data: 'daily'})
    dispatch('get_entity_data', value._id)
    router.push({ query: { _id: value._id } });
  }

  function handleStartDate (date) {
    commit('SET_START_DATE', date)
  }

  function handleCadence ({name, value}) {
    commit('SET_D_STATE', {key: name, data: value})
  }

  function handleAction ({name, value}) {
    switch (name) {
      case 'preview-img':
        filename.value = value
        break
      case 'edit-tags':
        const columnData = data.value.results.find(o => o.id === value)
        commit('HANDLE_SELECTED_COLUMN', {
          id: value,
          state: 'edit-tags',
          data: JSON.parse(JSON.stringify(columnData))
        })
        break
      case 'save-tags':
        dispatch('save_tags')
        break
      case 'cancel-tags-edit':
        commit('CANCEL_TAG_EDITING', {id: value})
        break
    }
  }

  function closePreview () {
    filename.value = ''
  }

  onUnmounted(() => {
    commit('HANDLE_SELECTED_COLUMN', {
      state: 'preview'
    })
  })
</script>

<style lang="scss">
  @import "../styles/vars";

  .dashboard-view {
    @media only screen and (min-width: 0) {
      padding: 14px;
      > header {
        padding-right: 120px;
        .date-picker {
          margin: 0 12px;
          .dp__main {
            border-radius: 4px;
            .dp__input {
              border: 1px solid rgb(118, 118, 118);
            }
          }
          i {
            margin-left: 4px;
          }
        }
      }

      .entity-columns {
        overflow-x: auto;
        padding-bottom: 20px;
        min-height: 40%;
        section {
          //max-width: 550px;
          margin-right: 24px;
          background-color: white;
          border-radius: 4px;
          flex: 1 0 auto;
          color: $dark-brown;
        }
        .img-preview {
          width: 680px;
          height: 100%;
          left: 690px;
          z-index: 9;
          border: 1px solid black;
          figure {
            width: 100%;
            overflow-y: scroll;
          }
          img {
            width: 180%;
          }
        }
        a {
          color: #001E96;
          text-decoration: none;
        }
        h3 {
          font-size: 1.8rem;
          color: $black;
        }

        .select-entity-bg-text {
          font-size: 8rem;
          opacity: 0.05;
          pointer-events: none;
          margin-top: 200px;
        }
      }
    }
  }
</style>